import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { getGridItemVariants } from '@/lib/animations';
import { useParallaxRef } from '@/utils/parallax';
import { supabase } from '@/integrations/supabase/client';

interface ProfileTabProps {
  userEmail: string;
  name: string;
  phone: string;
  address: string;
  saving: boolean;
  onNameChange: (name: string) => void;
  onPhoneChange: (phone: string) => void;
  onAddressChange: (address: string) => void;
  onSave: (e: React.FormEvent) => void;
}

export default function ProfileTab({
  userEmail,
  name,
  phone,
  address,
  saving,
  onNameChange,
  onPhoneChange,
  onAddressChange,
  onSave,
}: ProfileTabProps) {
  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangingEmail(true);
    // TODO: Implement email change functionality
    console.log('Change email:', { newEmail, confirmEmail, emailPassword });
    setTimeout(() => {
      setChangingEmail(false);
      setNewEmail('');
      setConfirmEmail('');
      setEmailPassword('');
    }, 1000);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangingPassword(true);
    // TODO: Implement password change functionality
    console.log('Change password:', { currentPassword, newPassword, confirmPassword });
    setTimeout(() => {
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }, 1000);
  };

  const navigate = useNavigate();

  // Reveal hooks for each card section
  const contactReveal = useRevealOnce({ amount: 0.3 });
  const emailReveal = useRevealOnce({ amount: 0.3 });
  const passwordReveal = useRevealOnce({ amount: 0.3 });
  const notificationsReveal = useRevealOnce({ amount: 0.3 });
  const logoutReveal = useRevealOnce({ amount: 0.3 });

  // Parallax refs for subtitles
  const contactSubRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });
  const emailSubRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });
  const passwordSubRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });
  const notificationsSubRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });
  const logoutSubRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/account/login', { replace: true });
    } catch (error) {
      console.error('[account] Logout error:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Contact Information Card */}
      <motion.div
        ref={contactReveal.ref}
        variants={getGridItemVariants(0)}
        initial="hidden"
        animate={contactReveal.controls}
        className="apple-card p-6 md:p-8"
      >
        <CardHeader className="px-0 pt-0 text-center pb-12">
          <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Contact Information</CardTitle>
          <CardDescription ref={contactSubRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
            Update your contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-4">
          <form onSubmit={onSave} className="space-y-4">
            <div>
              <Label htmlFor="current-email">Current Email</Label>
              <Input id="current-email" type="email" value={userEmail} disabled />
              <p className="text-xs text-muted-foreground mt-1">Use the form below to change your email</p>
            </div>

            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => onPhoneChange(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                type="text"
                value={address}
                onChange={(e) => onAddressChange(e.target.value)}
                placeholder="Enter your address"
              />
            </div>

            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </CardContent>
      </motion.div>

      {/* Change Email Address Card */}
      <motion.div
        ref={emailReveal.ref}
        variants={getGridItemVariants(1)}
        initial="hidden"
        animate={emailReveal.controls}
        className="apple-card p-6 md:p-8"
      >
        <CardHeader className="px-0 pt-0 text-center pb-12">
          <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Change Email Address</CardTitle>
          <CardDescription ref={emailSubRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
            Update your email address
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-4">
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div>
              <Label htmlFor="new-email">New Email</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirm-email">Confirm New Email</Label>
              <Input
                id="confirm-email"
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="Confirm new email address"
                required
              />
            </div>

            <div>
              <Label htmlFor="email-password">Current Password</Label>
              <Input
                id="email-password"
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                placeholder="Enter your current password"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter your current password to confirm the email change
              </p>
            </div>

            <Button type="submit" variant="outline" disabled={changingEmail}>
              {changingEmail ? 'Changing Email...' : 'Change Email'}
            </Button>
          </form>
        </CardContent>
      </motion.div>

      {/* Change Password Card */}
      <motion.div
        ref={passwordReveal.ref}
        variants={getGridItemVariants(2)}
        initial="hidden"
        animate={passwordReveal.controls}
        className="apple-card p-6 md:p-8"
      >
        <CardHeader className="px-0 pt-0 text-center pb-12">
          <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Change Password</CardTitle>
          <CardDescription ref={passwordSubRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
            Update your account password
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-4">
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
              />
            </div>

            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Password must be at least 8 characters long
              </p>
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>

            <Button type="submit" variant="outline" disabled={changingPassword}>
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </motion.div>

      {/* Notifications Card */}
      <motion.div
        ref={notificationsReveal.ref}
        variants={getGridItemVariants(3)}
        initial="hidden"
        animate={notificationsReveal.controls}
        className="apple-card p-6 md:p-8"
      >
        <CardHeader className="px-0 pt-0 text-center pb-12">
          <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Notifications</CardTitle>
          <CardDescription ref={notificationsSubRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
            Choose which emails and reminders you receive
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="billing-alerts">Billing & payment alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications about payments and billing updates
                </p>
              </div>
              <input
                id="billing-alerts"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                disabled
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="domain-reminders">Domain and lease reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Get reminders about your domains and lease payments
                  </p>
                </div>
                <input
                  id="domain-reminders"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  disabled
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="product-updates">Product updates & tips</Label>
                  <p className="text-sm text-muted-foreground">
                    Stay informed about new features and helpful tips
                  </p>
                </div>
                <input
                  id="product-updates"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  disabled
                />
              </div>
            </div>
          </div>
        </CardContent>
      </motion.div>

      {/* Logout Card */}
      <motion.div
        ref={logoutReveal.ref}
        variants={getGridItemVariants(4)}
        initial="hidden"
        animate={logoutReveal.controls}
        className="apple-card p-6 md:p-8"
      >
        <CardHeader className="px-0 pt-0 text-center pb-12">
          <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Logout</CardTitle>
          <CardDescription ref={logoutSubRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
            Sign out of Attain Domains
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-4">
          <div className="flex justify-center">
            <Button variant="outline" onClick={handleLogout} className="w-full sm:w-auto">
              Logout
            </Button>
          </div>
        </CardContent>
      </motion.div>
    </div>
  );
}

